import { Box, Stack, Typography } from '@mui/material';
import { isEmpty, sortBy } from 'lodash';
import { useCallback, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { Loader } from '@/components/progress/Loader/Loader';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useEstatePermissions } from '@/modules/authentication/hooks/useEstatePermissions';
import { canAddPresentationBundle } from '@/modules/presentation/clientPresentation/utils/canAddPresentationBundle';
import { useSystemPresentationTemplateWhere } from '@/modules/presentationTemplates/hooks/useSystemPresentationTemplateWhere';
import { isVisibleFirmPresentationTemplate } from '@/modules/presentationTemplates/presentationTemplate.utils';
import { NEW_TEMPLATE_SENTINEL } from '@/modules/presentationTemplates/PresentationTemplateEditor/PresentationTemplateEditor.constants';
import { sortSystemTemplates } from '@/modules/presentationTemplates/systemTemplateOrder.utils';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getNodes } from '@/utils/graphqlUtils';
import { getPublicImageUrl } from '@/utils/staticFiles';

import {
  PresentationTemplateSelection_PresentationTemplateFragment,
  usePresentationTemplateSelectionQuery,
  useStartCreatePresentationFromTemplateMutation,
} from './graphql/PresentationTemplateSelection.generated';
import { PresentationTemplateSelectionModalDisplayItem } from './PresentationTemplateSelectionModal.components';

export interface PresentationTemplateSelectionModalProps {
  householdId: string;
  onClose: () => void;
}

export function PresentationTemplateSelectionModal({
  householdId,
  onClose,
}: PresentationTemplateSelectionModalProps) {
  const currentUser = useCurrentUser();
  const { canPresentEstateWaterfall } = useEstatePermissions();
  const { id: tenantId } = useTenantDetailsContext();
  const { navigate } = useNavigateToRoute();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const [createPresentation, { loading: creating }] =
    useStartCreatePresentationFromTemplateMutation({
      onCompleted: (data) => {
        const presentationId =
          data.startCreatePresentationFromTemplate?.presentation?.id;
        if (presentationId) {
          navigate(ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2, {
            householdId,
            presentationId,
          });
          onClose();
        }
      },
    });

  const handleUseTemplate = async () => {
    if (!selectedTemplateId || !selectedTemplateIsAvailable) return;
    await createPresentation({
      variables: {
        templateID: selectedTemplateId,
        householdID: householdId,
      },
    });
  };

  const systemTemplateWhere = useSystemPresentationTemplateWhere();

  const { data, loading: loadingQuery } = usePresentationTemplateSelectionQuery(
    {
      variables: {
        currentUserId: currentUser?.id ?? '',
        systemTemplateWhere,
      },
      skip: !currentUser?.id,
      fetchPolicy: 'cache-and-network',
    }
  );

  // With cache-and-network, loading is true even when cached data is available.
  // Only show the loader when there's no data at all (first fetch).
  const loading = loadingQuery && !data;

  const canUseTemplate = (
    template: PresentationTemplateSelection_PresentationTemplateFragment
  ) =>
    getNodes(template.bundles).every((bundle) =>
      canAddPresentationBundle(bundle.kind, canPresentEstateWaterfall)
    );

  const userTemplates = sortBy(
    getNodes(data?.userTemplates).filter(canUseTemplate),
    'displayName'
  );
  const tenantTemplates = sortBy(
    getNodes(data?.tenantTemplates).filter(
      (template) =>
        isVisibleFirmPresentationTemplate(template, tenantId) &&
        canUseTemplate(template)
    ),
    'displayName'
  );
  const luminaryTemplates = sortSystemTemplates(
    getNodes(data?.luminaryTemplates).filter(canUseTemplate)
  );

  const hasNoTemplates =
    isEmpty(userTemplates) &&
    isEmpty(tenantTemplates) &&
    isEmpty(luminaryTemplates) &&
    !loading;

  const selectedTemplateIsAvailable = [
    ...userTemplates,
    ...tenantTemplates,
    ...luminaryTemplates,
  ].some((template) => template.id === selectedTemplateId);

  const handleCreateTemplate = useCallback(() => {
    navigate(
      ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_TEMPLATES_USER_EDITOR,
      {
        householdId,
        templateId: NEW_TEMPLATE_SENTINEL,
      },
      { isFromPresentationList: 'true' }
    );
    onClose();
  }, [householdId, navigate, onClose]);

  return (
    <DialogModal
      isOpen
      onClose={onClose}
      heading="Choose a template"
      dialogPaperSx={{ maxWidth: 848 }}
      actions={
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
        >
          <Button onClick={onClose} variant="secondary" size="sm">
            Cancel
          </Button>
          {hasNoTemplates ? (
            <Button onClick={handleCreateTemplate} variant="primary" size="sm">
              Create your first template
            </Button>
          ) : (
            <Button
              onClick={handleUseTemplate}
              variant="primary"
              size="sm"
              disabled={
                creating || !selectedTemplateId || !selectedTemplateIsAvailable
              }
              loading={creating}
            >
              Use template
            </Button>
          )}
        </Stack>
      }
    >
      <SelectionModalBody
        loading={loading}
        hasNoTemplates={hasNoTemplates}
        userTemplates={userTemplates}
        tenantTemplates={tenantTemplates}
        luminaryTemplates={luminaryTemplates}
        selectedTemplateId={selectedTemplateId}
        onSelectTemplate={setSelectedTemplateId}
      />
    </DialogModal>
  );
}

interface SelectionModalBodyProps {
  loading: boolean;
  hasNoTemplates: boolean;
  userTemplates: PresentationTemplateSelection_PresentationTemplateFragment[];
  tenantTemplates: PresentationTemplateSelection_PresentationTemplateFragment[];
  luminaryTemplates: PresentationTemplateSelection_PresentationTemplateFragment[];
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string) => void;
}

function SelectionModalBody({
  loading,
  hasNoTemplates,
  userTemplates,
  tenantTemplates,
  luminaryTemplates,
  selectedTemplateId,
  onSelectTemplate,
}: SelectionModalBodyProps) {
  if (loading) {
    return (
      <Loader
        boxProps={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      />
    );
  }

  if (hasNoTemplates) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={3}
        px={12}
        py={6}
      >
        <Box
          component="img"
          src={getPublicImageUrl('/empty-presentation-template.svg')}
          alt=""
          width={281}
        />
        <Typography variant="body-s">
          You don&apos;t have any templates available.
        </Typography>
      </Stack>
    );
  }

  const sections = [
    { title: 'My templates', templates: userTemplates },
    { title: 'Firm templates', templates: tenantTemplates },
    { title: 'Luminary templates', templates: luminaryTemplates },
  ];

  return (
    <Stack spacing={1.5}>
      <Typography variant="body-s">
        Select a template for your presentation:
      </Typography>
      <Stack spacing={2}>
        {sections.map(({ title, templates }) =>
          templates.length > 0 ? (
            <Stack key={title}>
              <Typography variant="title-s" sx={{ pb: 1 }}>
                {title}
              </Typography>
              {templates.map((template, index) => (
                <PresentationTemplateSelectionModalDisplayItem
                  key={template.id}
                  template={template}
                  isSelected={template.id === selectedTemplateId}
                  isLastInSection={index === templates.length - 1}
                  onClick={() => onSelectTemplate(template.id)}
                />
              ))}
            </Stack>
          ) : null
        )}
      </Stack>
    </Stack>
  );
}
